import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { IPagination } from '@/store/types/search.types';

import { TAppError } from '@/store/types/base.state';

import {
  ISearchWallet,
  IWallet,
  IWalletAccountDetailsDownload,
  IWalletDomiciliationCertificateDownload,
  IWalletBalanceCertificateDownload,
  IWalletClosureCertficateDownload,
} from '@/features/wallets/store/models/wallet.model';
import { WalletService } from '@/features/wallets/services/WalletService';
import {
  IAgentWalletsState,
  TAgentWalletsAction,
  agentWalletsActionTypes,
} from '@/features/wallets/store/types/agent-wallets.types';

export const searchAgentWallets = (
  search?: ISearchWallet,
): ThunkAction<Promise<void>, IAgentWalletsState, {}, TAgentWalletsAction> => {
  const request = (): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.SEARCH_AGENT_WALLETS,
  });
  const fail = (error: TAppError): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.SEARCH_AGENT_WALLETS_FAIL,
    error,
    shouldSkipToaster: true,
    documentation: 'https://docs.treezor.com/api-reference/api-endpoints.html#tag/Wallets/getWallets',
  });
  const success = (wallets: IWallet[], pagination: IPagination): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.SEARCH_AGENT_WALLETS_SUCCESS,
    shouldSkipToaster: true,
    wallets,
    pagination,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TAgentWalletsAction>): Promise<void> => {
    dispatch(request());
    try {
      const wallets = await WalletService.searchWallets(search);

      const pagination: IPagination = {
        currentPage: search?.pageNumber || 1,
        totalPages: Math.trunc((wallets[0]?.totalRows || wallets.length) / (search?.pageCount || 50) || 0) + 1,
      };

      dispatch(success(wallets, pagination));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getAgentWalletAccountDetail = (
  walletId: number,
): ThunkAction<Promise<void>, IAgentWalletsState, {}, TAgentWalletsAction> => {
  const request = (): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_ACCOUNT_DETAILS,
    shouldSkipToaster: true,
    walletId,
  });
  const fail = (error: TAppError): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_ACCOUNT_DETAILS_FAIL,
    error,
    walletId,
  });
  const success = (walletAccountDetail: IWalletAccountDetailsDownload): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_ACCOUNT_DETAILS_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    walletAccountDetail,
  });

  return async (dispatch: ThunkDispatch<IAgentWalletsState, {}, TAgentWalletsAction>): Promise<void> => {
    dispatch(request());
    try {
      const walletAccountDetail = await WalletService.getWalletAccountDetail(walletId);
      dispatch(success(walletAccountDetail));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getAgentDomiciliationCertificate = (
  walletId: number,
): ThunkAction<Promise<void>, IAgentWalletsState, {}, TAgentWalletsAction> => {
  const request = (): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_DOMICILIATION_CERTIFICATE,
    shouldSkipToaster: true,
    walletId,
  });
  const fail = (error: TAppError): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_DOMICILIATION_CERTIFICATE_FAIL,
    error,
    walletId,
  });
  const success = (domiciliationCertificate: IWalletDomiciliationCertificateDownload): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_DOMICILIATION_CERTIFICATE_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    domiciliationCertificate,
  });

  return async (dispatch: ThunkDispatch<IAgentWalletsState, {}, TAgentWalletsAction>): Promise<void> => {
    dispatch(request());
    try {
      const domiciliationCertificate = await WalletService.getDomiciliationCertficate(walletId);
      dispatch(success(domiciliationCertificate));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getAgentBalanceCertificate = (
  walletId: number,
): ThunkAction<Promise<void>, IAgentWalletsState, {}, TAgentWalletsAction> => {
  const request = (): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_BALANCE_CERTIFICATE,
    shouldSkipToaster: true,
    walletId,
  });
  const fail = (error: TAppError): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_BALANCE_CERTIFICATE_FAIL,
    error,
    walletId,
  });
  const success = (balanceCertificate: IWalletBalanceCertificateDownload): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_BALANCE_CERTIFICATE_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    balanceCertificate,
  });

  return async (dispatch: ThunkDispatch<IAgentWalletsState, {}, TAgentWalletsAction>): Promise<void> => {
    dispatch(request());
    try {
      const balanceCertificate = await WalletService.getBalanceCertificate(walletId);
      dispatch(success(balanceCertificate));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getAgentClosureCertificate = (
  walletId: number,
): ThunkAction<Promise<void>, IAgentWalletsState, {}, TAgentWalletsAction> => {
  const request = (): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_CLOSURE_CERTIFICATE,
    shouldSkipToaster: true,
    walletId,
  });
  const fail = (error: TAppError): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_CLOSURE_CERTIFICATE_FAIL,
    error,
    walletId,
  });
  const success = (closureCertificate: IWalletClosureCertficateDownload): TAgentWalletsAction => ({
    type: agentWalletsActionTypes.GET_AGENT_WALLET_CLOSURE_CERTIFICATE_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    closureCertificate,
  });

  return async (dispatch: ThunkDispatch<IAgentWalletsState, {}, TAgentWalletsAction>): Promise<void> => {
    dispatch(request());
    try {
      const closureCertificate = await WalletService.getClosureCertificate(walletId);
      dispatch(success(closureCertificate));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
